import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import type { PixelPoint } from '../domain/bonds';
import { pinchViewport, type ViewportTransform } from '../domain/viewport';

type Event = ReactPointerEvent<HTMLDivElement>;

interface Navigation {
  readonly start: ViewportTransform;
  readonly midpoint: PixelPoint;
  readonly distance: number;
}

// #762 / #612 -- multi-finger touch on the paper (docs/model/drawing-ui-input-accessibility.md):
// one finger uses the active tool (the existing handlers); a second finger cancels any unfinished
// edit, then two fingers pan and pinch-zoom; three or more do nothing. Once fingers have navigated,
// none of them draws until every finger has lifted, so extra fingers never leave marks.
// Pen-only mode (#762): fingers only navigate -- one finger pans, two pan/pinch -- and the pen uses
// the tool. While the pen is down, touches are ignored (a resting palm never pans or marks).
// Each handler returns true when it consumed the event.
export function usePaperTouchNavigation({
  viewportRef,
  setViewport,
  onCancelEdits,
  penOnly = false
}: {
  readonly viewportRef: RefObject<ViewportTransform>;
  readonly setViewport: (viewport: ViewportTransform) => void;
  readonly onCancelEdits: () => void;
  readonly penOnly?: boolean;
}) {
  const touchesRef = useRef(new Map<number, PixelPoint>());
  const navigationRef = useRef<Navigation | null>(null);
  // Set from the second finger (the first, in pen-only mode) until every finger has lifted.
  const engagedRef = useRef(false);
  // Pen-only palm rejection: whether the pen is down, and touches to ignore until they lift.
  const penDownRef = useRef(false);
  const ignoredRef = useRef(new Set<number>());

  const local = (event: Event): PixelPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  // Two fingers pinch about their midpoint; a single finger (pen-only mode) just pans.
  const startNavigation = () => {
    const [a, b] = [...touchesRef.current.values()];
    const second = b ?? a;
    navigationRef.current =
      a && second
        ? {
            start: viewportRef.current,
            midpoint: { x: (a.x + second.x) / 2, y: (a.y + second.y) / 2 },
            distance: Math.hypot(second.x - a.x, second.y - a.y)
          }
        : null;
  };
  // How many fingers navigate: two normally; one or two in pen-only mode.
  const navigates = (count: number) => count === 2 || (penOnly && count === 1);

  const pointerDown = (event: Event): boolean => {
    if (event.pointerType !== 'touch') return false;
    if (penOnly && penDownRef.current) {
      ignoredRef.current.add(event.pointerId);
      event.preventDefault();
      return true;
    }
    touchesRef.current.set(event.pointerId, local(event));
    if (touchesRef.current.size < 2 && !penOnly) return false;
    event.preventDefault();
    if (!engagedRef.current) {
      engagedRef.current = true;
      onCancelEdits();
    }
    // A third finger (or more) pauses navigation without acting.
    if (navigates(touchesRef.current.size)) startNavigation();
    else navigationRef.current = null;
    return true;
  };

  const pointerMove = (event: Event): boolean => {
    if (event.pointerType === 'touch' && ignoredRef.current.has(event.pointerId)) return true;
    if (event.pointerType !== 'touch' || !touchesRef.current.has(event.pointerId)) return false;
    touchesRef.current.set(event.pointerId, local(event));
    if (!engagedRef.current) return false;
    event.preventDefault();
    const navigation = navigationRef.current;
    const [a, b] = [...touchesRef.current.values()];
    const second = b ?? a;
    if (navigation && a && second && navigates(touchesRef.current.size)) {
      setViewport(
        pinchViewport(
          navigation.start,
          navigation.midpoint,
          navigation.distance,
          { x: (a.x + second.x) / 2, y: (a.y + second.y) / 2 },
          Math.hypot(second.x - a.x, second.y - a.y)
        )
      );
    }
    return true;
  };

  // pointerup / pointercancel for a touch.
  const pointerEnd = (event: Event): boolean => {
    if (event.pointerType === 'touch' && ignoredRef.current.delete(event.pointerId)) return true;
    if (event.pointerType !== 'touch' || !touchesRef.current.has(event.pointerId)) return false;
    touchesRef.current.delete(event.pointerId);
    const engaged = engagedRef.current;
    // Back down to a navigating count: carry on from where the fingers are now.
    if (engaged && navigates(touchesRef.current.size)) startNavigation();
    else navigationRef.current = null;
    if (touchesRef.current.size === 0) engagedRef.current = false;
    return engaged;
  };

  const reset = useCallback(() => {
    touchesRef.current.clear();
    ignoredRef.current.clear();
    navigationRef.current = null;
    engagedRef.current = false;
    penDownRef.current = false;
  }, []);

  // The pen touched down / lifted (pen-only palm rejection): fingers already resting are ignored
  // until they lift, and any navigation they were doing stops.
  const penDown = () => {
    penDownRef.current = true;
    if (!penOnly) return;
    for (const id of touchesRef.current.keys()) ignoredRef.current.add(id);
    touchesRef.current.clear();
    navigationRef.current = null;
    engagedRef.current = false;
  };
  const penUp = () => {
    penDownRef.current = false;
  };

  return { pointerDown, pointerMove, pointerEnd, reset, penDown, penUp, isNavigating: () => engagedRef.current };
}
